import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  MenuItem,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Fade,
  useMediaQuery,
  useTheme,
  Divider,
  Stack
} from "@mui/material";
import {
  Search as SearchIcon,
  BatteryChargingFull,
  ElectricBolt,
  LocationOn,
  CalendarToday,
  TrendingUp,
  AttachMoney,
  Business,
  Assessment
} from '@mui/icons-material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChargingStation,
  faCalendarAlt,
  faMapMarkerAlt,
  faBolt,
  faCoins,
  faBuilding
} from "@fortawesome/free-solid-svg-icons";
import { useMovilidadElectrica } from "../../context/MovilidadElectricaContext";

const MatrizConsumosList = () => {
  const { consumptionData, refreshConsumptionData, parkers, refreshParkers, loading, error } = useMovilidadElectrica();
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    location: "all",
  });
  const [showInfo, setShowInfo] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  useEffect(() => {
    refreshParkers();
  }, [refreshParkers]);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      setShowInfo(false);
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        location: filters.location !== "all" ? filters.location : null,
      };
      Object.keys(params).forEach(key => params[key] == null && delete params[key]);
      refreshConsumptionData(params);
    } else {
      setShowInfo(true);
      // Opcional: limpiar datos si las fechas se quitan
      // refreshConsumptionData({ data: [], total_consumption: 0 }); 
    }
  }, [filters, refreshConsumptionData]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Datos que no se pueden mapear se dejan con valores genéricos
  const totalConsumo = consumptionData.total_consumption ? `${consumptionData.total_consumption} KW/H` : "0 KW/H";
  const totalDinero = "N/A"; // No viene del endpoint
  const totalCopropiedad = "N/A"; // No viene del endpoint

  return (
    <Fade in timeout={800}>
      <Box>
        {/* Filtros modernos */}
        <Card 
          sx={{ 
            mb: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
            border: '1px solid rgba(0, 38, 66, 0.06)'
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: { xs: 2, sm: 2.5 }
            }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SearchIcon sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#002642',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.125rem' }
                }}
              >
                Filtros de Consulta
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#002642',
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faCalendarAlt} 
                    style={{ color: '#DB9600', fontSize: '0.9rem' }}
                  />
                  Fecha de Inicio
                </Typography>
                <TextField
                  name="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#002642',
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faCalendarAlt} 
                    style={{ color: '#DB9600', fontSize: '0.9rem' }}
                  />
                  Fecha de Fin
                </Typography>
                <TextField
                  name="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#002642',
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faMapMarkerAlt} 
                    style={{ color: '#DB9600', fontSize: '0.9rem' }}
                  />
                  Celda de Carga
                </Typography>
                <TextField
                  name="location"
                  select
                  value={filters.location}
                  onChange={handleFilterChange}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    }
                  }}
                >
                  <MenuItem value="all">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon 
                        icon={faChargingStation} 
                        style={{ color: '#75787B', fontSize: '0.8rem' }}
                      />
                      Todas las celdas
                    </Box>
                  </MenuItem>
                  {(parkers.data || []).map(parker => (
                    <MenuItem key={parker.parker_id} value={parker.location}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faChargingStation} 
                          style={{ color: '#DB9600', fontSize: '0.8rem' }}
                        />
                        {parker.location}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contenido principal */}
        {loading ? (
          <Fade in timeout={600}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2
            }}>
              <CircularProgress 
                size={60} 
                sx={{ color: '#002642' }}
              />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#75787B',
                  textAlign: 'center'
                }}
              >
                Generando matriz de consumos...
              </Typography>
            </Box>
          </Fade>
        ) : showInfo ? (
          <Fade in timeout={600}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
              border: '2px dashed rgba(0, 38, 66, 0.1)',
              mb: 3
            }}>
              <CardContent sx={{ py: 6, textAlign: 'center' }}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(0, 38, 66, 0.05) 0%, rgba(0, 87, 122, 0.05) 100%)',
                    display: 'inline-flex',
                    mb: 3
                  }}
                >
                  <Assessment sx={{ 
                    color: '#75787B', 
                    fontSize: '3rem'
                  }} />
                </Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#002642',
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  Matriz de Consumos
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#75787B',
                    maxWidth: 400,
                    mx: 'auto'
                  }}
                >
                  Selecciona un rango de fechas para generar el análisis detallado de consumo energético por celda.
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        ) : error ? (
          <Fade in timeout={600}>
            <Alert 
              severity="error"
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(211, 47, 47, 0.2)',
                mb: 3,
                '& .MuiAlert-icon': {
                  color: '#d32f2f'
                }
              }}
            >
              Error al cargar los datos de consumo: {error.message}
            </Alert>
          </Fade>
        ) : (
          <Fade in timeout={800}>
            <Box>
              {/* Vista desktop: Tabla */}
              {!isMobile && (
                <TableContainer 
                  component={Paper}
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
                    border: '1px solid rgba(0, 38, 66, 0.06)',
                    overflow: 'hidden',
                    mb: 3
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(0, 38, 66, 0.02)' }}>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: '#002642',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Consumo Diario
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: '#002642',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Celda
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: '#002642',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Fecha
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          color: '#002642',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Consumo Mensual
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consumptionData.data.map((consumo, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 38, 66, 0.02)',
                              transform: 'scale(1.001)'
                            }
                          }}
                        >
                          <TableCell sx={{ 
                            color: '#002642', 
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FontAwesomeIcon 
                                icon={faBolt} 
                                style={{ color: '#DB9600', fontSize: '0.8rem' }}
                              />
                              {consumo.daily_consumption} kW/h
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            color: '#75787B', 
                            fontSize: '0.875rem'
                          }}>
                            <Chip 
                              icon={<LocationOn sx={{ fontSize: '1rem' }} />}
                              label={consumo.location}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(0, 38, 66, 0.1)',
                                color: '#002642',
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                  color: '#002642'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            color: '#75787B', 
                            fontSize: '0.875rem'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FontAwesomeIcon 
                                icon={faCalendarAlt} 
                                style={{ color: '#DB9600', fontSize: '0.8rem' }}
                              />
                              {consumo.date}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            color: '#002642', 
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TrendingUp sx={{ color: '#DB9600', fontSize: '1rem' }} />
                              {consumo.monthly_consumption} kW/h
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Vista mobile: Cards */}
              {isMobile && (
                <Box sx={{ mb: 3 }}>
                  {consumptionData.data.map((consumo, index) => (
                    <Fade in timeout={600} key={index}>
                      <Card 
                        sx={{ 
                          mb: 2, 
                          borderRadius: 3,
                          boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
                          border: '1px solid rgba(0, 38, 66, 0.06)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          overflow: 'hidden',
                          '&:hover': {
                            boxShadow: '0 8px 32px rgba(0, 38, 66, 0.12)',
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          {/* Header del card */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                          }}>
                            <Chip 
                              icon={<LocationOn sx={{ fontSize: '1rem' }} />}
                              label={consumo.location}
                              sx={{
                                backgroundColor: 'rgba(0, 38, 66, 0.1)',
                                color: '#002642',
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                  color: '#002642'
                                }
                              }}
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#75787B',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <FontAwesomeIcon 
                                icon={faCalendarAlt} 
                                style={{ fontSize: '0.7rem' }}
                              />
                              {consumo.date}
                            </Typography>
                          </Box>

                          {/* Métricas */}
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ 
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: 'rgba(219, 150, 0, 0.05)',
                                border: '1px solid rgba(219, 150, 0, 0.1)',
                                textAlign: 'center'
                              }}>
                                <FontAwesomeIcon 
                                  icon={faBolt} 
                                  style={{ 
                                    color: '#DB9600', 
                                    fontSize: '1.2rem',
                                    marginBottom: '8px'
                                  }} 
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#75787B', 
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    mb: 0.5
                                  }}
                                >
                                  Consumo Diario
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    color: '#002642',
                                    fontWeight: 600,
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {consumo.daily_consumption} kW/h
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ 
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0, 38, 66, 0.02)',
                                border: '1px solid rgba(0, 38, 66, 0.06)',
                                textAlign: 'center'
                              }}>
                                <TrendingUp sx={{ 
                                  color: '#002642', 
                                  fontSize: '1.2rem',
                                  mb: 1
                                }} />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#75787B', 
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    mb: 0.5
                                  }}
                                >
                                  Consumo Mensual
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    color: '#002642',
                                    fontWeight: 600,
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {consumo.monthly_consumption} kW/h
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Fade>
                  ))}
                </Box>
              )}
            </Box>
          </Fade>
        )}

        {/* Resumen de estadísticas */}
        {!showInfo && !loading && !error && consumptionData.data && consumptionData.data.length > 0 && (
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
            border: '1px solid rgba(0, 38, 66, 0.06)',
            background: 'linear-gradient(135deg, rgba(0, 38, 66, 0.02) 0%, rgba(0, 87, 122, 0.02) 100%)'
          }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3
              }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Assessment sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#002642',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  Resumen de Consumo
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Total de consumo */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(0, 38, 66, 0.06)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0, 38, 66, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <FontAwesomeIcon 
                      icon={faBolt} 
                      style={{ 
                        color: '#DB9600', 
                        fontSize: '2rem',
                        marginBottom: '12px'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#75787B', 
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      Total Consumo
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: '#002642',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        mb: 0.5
                      }}
                    >
                      {totalConsumo}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#75787B',
                        fontSize: '0.8rem'
                      }}
                    >
                      En el rango consultado
                    </Typography>
                  </Box>
                </Grid>

                {/* Total dinero recaudado */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(0, 38, 66, 0.06)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0, 38, 66, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <FontAwesomeIcon 
                      icon={faCoins} 
                      style={{ 
                        color: '#DB9600', 
                        fontSize: '2rem',
                        marginBottom: '12px'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#75787B', 
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      Dinero Recaudado
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: '#002642',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        mb: 0.5
                      }}
                    >
                      {totalDinero}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#75787B',
                        fontSize: '0.8rem'
                      }}
                    >
                      Total facturado
                    </Typography>
                  </Box>
                </Grid>

                {/* Total copropiedad */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(0, 38, 66, 0.06)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0, 38, 66, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <FontAwesomeIcon 
                      icon={faBuilding} 
                      style={{ 
                        color: '#DB9600', 
                        fontSize: '2rem',
                        marginBottom: '12px'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#75787B', 
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      Copropiedad
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: '#002642',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        mb: 0.5
                      }}
                    >
                      {totalCopropiedad}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#75787B',
                        fontSize: '0.8rem'
                      }}
                    >
                      Fondos comunes
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

      </Box>
    </Fade>
  );
};

export default MatrizConsumosList;
